import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
  getMenuStatus,
  getUserMenuList,
} from "../../../application/selectors/user";
import {
  isAdminUser,
  canAccessContractorAssistedRegistration,
  canAccessContractorPortal,
  canManageContractorTeam,
  canAccessDistributionPortal,
} from "../../utils/authRoleHelpers";
import { isDistributionPortalPath } from "../../pages/EDC/edcNav";
import {
  fetchAndSyncEmergencyAccess,
  isEmergencyAccessActive,
  isUmRecoveryPath,
} from "../../../infrastructure/utils/emergencyAccess";
import {
  menuMatchesPathSegment,
  pathnameMatchesMenuTree,
} from "../../pages/SLN/shared/projectWorkspace/projectWorkspaceMenu";
import { Box, CircularProgress, Typography } from "@mui/material";

const PUBLIC_PATHS = [
  "/",
  "/home",
  "/profile",
  "/account/profile",
  "/account/reset-password",
  "/403",
  "/404",
  "/error",
];

const isPublicPath = (pathname) => {
  return PUBLIC_PATHS.some((publicPath) => {
    if (publicPath === "/") {
      return pathname === "/" || pathname === "";
    }
    return pathname.toLowerCase().startsWith(publicPath.toLowerCase());
  });
};

const isAdminOpsPath = (pathname) =>
  pathname.toLowerCase() === "/admin" ||
  pathname.toLowerCase().startsWith("/admin/");

/** Contractor Portal under /SLN/Contractor (+ legacy /SLN/ContractorPortal). */
const CONTRACTOR_PORTAL_FIRST_SEGMENTS = new Set([
  "dashboard",
  "projects",
  "contracts",
  "payments",
  "profile",
  "contractorportal",
]);

const normalizeContractorPath = (pathname) =>
  (pathname || "").toLowerCase().replace(/\/+$/, "");

const isContractorPortalPath = (pathname) => {
  const p = normalizeContractorPath(pathname);
  if (p.startsWith("/sln/contractorportal")) return true;
  if (p === "/sln/contractor") return true;
  if (!p.startsWith("/sln/contractor/")) return false;
  const first = p.slice("/sln/contractor/".length).split("/")[0];
  return CONTRACTOR_PORTAL_FIRST_SEGMENTS.has(first);
};

/** Team-member / primary assisted-registration cartable (no UM menu row required). */
const isContractorAssistedRegistrationPath = (pathname) => {
  const p = normalizeContractorPath(pathname);
  return (
    p === "/sln/contractor/assistedregistration" ||
    p.startsWith("/sln/contractor/assistedregistration/")
  );
};

const isContractorTeamPath = (pathname) => {
  const p = normalizeContractorPath(pathname);
  return p === "/sln/contractor/team" || p.startsWith("/sln/contractor/team/");
};

const findMenuItemByPath = (pathnames, menuList) => {
  let specificNode = menuList;

  for (const path of pathnames) {
    let item = null;

    if (specificNode.level === 1) {
      item = specificNode.child?.find((menu) =>
        menuMatchesPathSegment(menu, path)
      );
      if (!item && Array.isArray(specificNode.child)) {
        const flattened = [];
        for (const node of specificNode.child) {
          if (Array.isArray(node?.child)) {
            flattened.push(...node.child);
          }
        }
        item = flattened.find((menu) => menuMatchesPathSegment(menu, path));
      }
    } else if (Array.isArray(specificNode?.child)) {
      item = specificNode.child.find((menu) =>
        menuMatchesPathSegment(menu, path)
      );
    }

    if (item) {
      specificNode = item;
    }
  }

  return specificNode;
};

const evaluateAccess = (pathname, menuList) => {
  if (isPublicPath(pathname)) {
    return true;
  }

  if (isAdminOpsPath(pathname) && isAdminUser()) {
    return true;
  }

  if (isContractorPortalPath(pathname) && canAccessContractorPortal()) {
    return true;
  }

  if (
    isContractorAssistedRegistrationPath(pathname) &&
    canAccessContractorAssistedRegistration()
  ) {
    return true;
  }

  if (isContractorTeamPath(pathname) && canManageContractorTeam()) {
    return true;
  }

  if (isDistributionPortalPath(pathname) && canAccessDistributionPortal()) {
    return true;
  }

  const pathnames = pathname.split("/").filter(Boolean);
  if (pathnames.length === 0) {
    return true;
  }

  if (!menuList?.length) {
    return false;
  }

  if (pathnameMatchesMenuTree(pathname, menuList)) {
    return true;
  }

  const initialStructure = { child: menuList, level: 1 };
  const menuItem = findMenuItemByPath(pathnames, initialStructure);
  return Boolean(menuItem && menuItem !== initialStructure);
};

const ProtectedOutlet = () => {
  const location = useLocation();
  const menuList = useSelector(getUserMenuList);
  const menuStatus = useSelector(getMenuStatus);
  const pathToCheck = location.pathname;

  const [emergencyReady, setEmergencyReady] = useState(false);
  const [emergencyActive, setEmergencyActive] = useState(isEmergencyAccessActive());

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const active = await fetchAndSyncEmergencyAccess();
      if (!cancelled) {
        setEmergencyActive(active || isEmergencyAccessActive());
        setEmergencyReady(true);
      }
    };
    setEmergencyReady(isEmergencyAccessActive());
    sync();
    const timer = setInterval(sync, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [pathToCheck]);

  const umRecoveryAllowed =
    isAdminUser() &&
    isUmRecoveryPath(pathToCheck) &&
    (emergencyActive || isEmergencyAccessActive());

  const decision = useMemo(() => {
    if (isPublicPath(pathToCheck)) {
      return "allow";
    }

    if (isAdminOpsPath(pathToCheck) && isAdminUser()) {
      return "allow";
    }

    if (isContractorPortalPath(pathToCheck) && canAccessContractorPortal()) {
      return "allow";
    }

    if (
      isContractorAssistedRegistrationPath(pathToCheck) &&
      canAccessContractorAssistedRegistration()
    ) {
      return "allow";
    }

    if (isContractorTeamPath(pathToCheck) && canManageContractorTeam()) {
      return "allow";
    }

    if (isDistributionPortalPath(pathToCheck) && canAccessDistributionPortal()) {
      return "allow";
    }

    if (umRecoveryAllowed) {
      return "allow";
    }

    if (
      isAdminUser() &&
      isUmRecoveryPath(pathToCheck) &&
      !emergencyReady
    ) {
      return "wait";
    }

    if (menuStatus === "idle" || menuStatus === "loading") {
      return "wait";
    }

    return evaluateAccess(pathToCheck, menuList) ? "allow" : "deny";
  }, [pathToCheck, menuList, menuStatus, umRecoveryAllowed, emergencyReady]);

  if (decision === "wait") {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          در حال بارگذاری منو...
        </Typography>
      </Box>
    );
  }

  if (decision === "deny") {
    return (
      <Navigate
        to="/403"
        state={{
          from: location,
          message: "شما به این صفحه دسترسی ندارید",
        }}
        replace
      />
    );
  }

  return <Outlet />;
};

export default ProtectedOutlet;
